import * as M from "./Model"
import { cmd } from "elm-ts"
import { pipe } from "fp-ts/function"
import { Variant } from "@flypengine/myFlyp-lib"
import * as TE from "fp-ts/TaskEither"
import * as E from "fp-ts/Either"
import * as O from "fp-ts/Option"
import * as ET from "elm-ts/lib/Task"
import {
  AuthStatus,
  sendEmailLink,
  signIn,
  verifySignInCredentials,
} from "../Authorization"

export const initModel = (authStatus: AuthStatus = "Not Attempted") => ({
  loginDetails: M.initLoginModel(authStatus),
  logged: E.left(authStatus),
})

export const init: <msg>(
  config: M.Config<msg>
) => [M.Model, cmd.Cmd<M.Msg>] = _config => {
  return [
    initModel(),
    location.search
      ? cmd.of(M.Msg.loginEmailReq())
      : cmd.of(M.Msg.init("Login")),
  ]
}

export const update: <msg>(
  config: M.Config<msg>
) => (msg: M.Msg) => (model: M.Model) => [M.Model, cmd.Cmd<msg>] =
  config => msg => model =>
    pipe(
      msg,
      Variant.caseOf({
        [M._emailChanged]: inputValue => [
          {
            ...model,
            loginDetails: {
              ...model.loginDetails,
              email: pipe(
                inputValue,
                O.chain(email => (email.length === 0 ? O.none : O.some(email)))
              ),
            },
          },
          cmd.none,
        ],
        [M._init]: logged => [{ ...model, logged: E.left(logged) }, cmd.none],
        [M._loginEmailReq]: () => [
          { ...model, logged: E.left("Login Unverified") },
          pipe(
            TE.tryCatch(
              () => signIn(),
              error => error!["message"] as AuthStatus
            ),
            ET.attempt(result => config.wrapperMsg(M.Msg.loginEmailRes(result)))
          ),
        ],
        [M._loginEmailRes]: logged => {
          console.log(logged)
          return pipe(
            logged,
            E.match(
              authStatus => [
                {
                  logged,
                  loginDetails: M.initLoginModel(authStatus),
                },
                cmd.none,
              ],
              () => [model, cmd.none]
            )
          )
        },
        [M._loginReqMsg]: () => [
          pipe(
            model.loginDetails.email,
            O.match(
              () => ({
                ...model,
                loginDetails: {
                  ...model.loginDetails,
                  error: O.some("Please enter a valid email address"),
                },
              }),
              () => ({
                ...model,
                loginDetails: {
                  ...model.loginDetails,
                  error: O.none,
                },
              })
            )
          ),
          pipe(
            model.loginDetails.email,
            O.match(
              () => cmd.none,
              email =>
                pipe(
                  TE.tryCatch(
                    () => sendEmailLink(email),
                    error => error!["message"] as AuthStatus
                  ),
                  ET.perform(result =>
                    config.wrapperMsg(M.Msg.loginResMsg(result))
                  )
                )
            )
          ),
        ],
        [M._loginResMsg]: result => [
          pipe(
            result,
            E.match(
              authStatus => {
                switch (authStatus) {
                  case "Login Link Failed": {
                    return {
                      logged: E.left(authStatus),
                      loginDetails: {
                        ...model.loginDetails,
                        error: O.some("Please enter a valid email address"),
                      },
                    }
                  }

                  default: {
                    return { ...model, logged: E.left(authStatus) }
                  }
                }
              },
              _ => ({
                ...model,
                logged: E.left("Login Link Requested" as const),
              })
            )
          ),
          cmd.none,
        ],
        [M._verifyLoginEmailReq]: () =>
          pipe(
            model.loginDetails.email,
            O.match(
              () => [model, cmd.none],
              email => [
                model,
                pipe(
                  TE.tryCatch(
                    () => verifySignInCredentials(email),
                    error => error!["message"] as AuthStatus
                  ),
                  ET.perform(result => {
                    location.search = ""
                    return config.wrapperMsg(M.Msg.verifyLoginEmailRes(result))
                  })
                ),
              ]
            )
          ),

        [M._verifyLoginEmailRes]: result => {
          return pipe(
            result,
            E.match(
              authStatus => [
                {
                  loginDetails: M.initLoginModel(authStatus),
                  logged: E.left(authStatus),
                },
                cmd.none,
              ],
              _ => {
                return [model, cmd.none]
              }
            )
          )
        },
      })
    )
